import * as React from 'react'

import { GlobalEvents } from '../../../Other/GlobalEvents'
import { TestableInterface } from '../interfaces/TestableInterface'
import { makeTranslatable, TranslatableType } from '../interfaces/Translatable'
import LoadingEllipsis from '../LoadingEllipsis'
import './Button.scss'

export enum ButtonType {
  accept,
  normal,
  premium,
  link,
  cancel,
  filter,
  filterDropDown,
  red,
  acceptDisabled,
}

export enum StarColor {
  green,
  white,
}

export interface ButtonProps extends TestableInterface {
  label: TranslatableType
  type?: ButtonType
  action?: () => void
  automation?: string
  disabled?: boolean
  selected?: boolean
  premium?: boolean
  starColor?: StarColor
  className?: string
  img?: string
  showLoadingAnimation?: boolean
}

export interface ButtonState {
  menuOpen: boolean
}

class Button extends React.Component<ButtonProps, ButtonState> {
  constructor(props) {
    super(props)
    // Set default state
    this.state = {
      menuOpen: false,
    }
  }

  // Set default props
  public static defaultProps: ButtonProps = {
    label: 'Button',
    type: ButtonType.normal,
    automation: '',
    disabled: false,
    selected: false,
    premium: false,
    starColor: StarColor.white,
    action: () => {},
  }

  /**
   * Internal function for button to react to click events and only accept single one at a time
   *
   * @param e
   */
  private reactToClick(e: Event) {
    if (!this.props.disabled && !this.props.showLoadingAnimation) {
      this.props.action()
    }
  }

  private e

  public componentDidMount() {
    this.e = GlobalEvents.Click((e) => {
      this.setState({ menuOpen: false })
    })
  }

  public componentWillUnmount() {
    GlobalEvents.unBind(this.e)
  }

  public render(): JSX.Element {
    const {
      automation,
      className,
      type,
      disabled,
      label,
      action,
      selected,
      premium,
      starColor,
      showLoadingAnimation = false,
      children,
    } = this.props

    const classes = ['Button', ButtonType[type]]
    if (className) {
      classes.push(className)
    }
    let star: JSX.Element = null
    if (disabled || showLoadingAnimation) {
      classes.push('disabled')
    }

    if (selected) {
      classes.push('selected')
    }

    if (premium) {
      if (starColor === StarColor.green) {
        star = <img className="premium-star" src={require('images/premiumfeature/premium-star-green@2x.png')} />
      } else {
        star = <img className="premium-star" src={require('images/premiumfeature/premium-star-white@2x.png')} />
      }
    }

    let img: JSX.Element = null
    if (this.props.img) {
      img = <img className={'icon'} src={this.props.img} />
    }

    const numChildren = React.Children.count(children)

    if (numChildren > 0) {
      let menu = null
      if (this.state.menuOpen) {
        menu = (
          <div
            className="button-menu "
            onClick={() => {
              this.setState({ menuOpen: false })
            }}
          >
            {children}
          </div>
        )
      }

      return (
        <div className={classes.join(' ') + ' button-menu-div'}>
          <div
            data-automation={automation}
            className={'menu-base'}
            onClick={() => {
              if (!showLoadingAnimation) {
                action()
              }
              this.setState({ menuOpen: false })
            }}
          >
            {showLoadingAnimation ? (
              <LoadingEllipsis />
            ) : (
              <>
                {star}
                {makeTranslatable(label)}
              </>
            )}
          </div>
          <div
            data-automation={automation + '-menu'}
            className={'dropdown'}
            onClick={(e) => {
              this.setState({ menuOpen: !this.state.menuOpen })
              GlobalEvents.stop(e)
              return false
            }}
          ></div>
          {menu}
        </div>
      )
    } else {
      return (
        <div
          data-automation={automation}
          className={classes.join(' ')}
          role="button"
          onClick={this.reactToClick.bind(this)}
        >
          {showLoadingAnimation ? (
            <LoadingEllipsis />
          ) : (
            <>
              {img}
              {star}
              {makeTranslatable(label)}
            </>
          )}
        </div>
      )
    }
  }
}

export default Button
